/**
 * Client-side crash reporting → Bugsink, via our tiny dependency-free reporter
 * (see ../../shared/reporter.ts) instead of the ~90 KB-gzipped `@sentry/browser`
 * SDK. Init happens on import (first import in `main.ts`) so boot-time failures
 * and the store (constructed at import) are covered.
 *
 * The store enriches reports with live gameplay context (see `setSentryPlayer` /
 * `setSentryGame` / `setSentryContext` / `sentryBreadcrumb`).
 */
import { createReporter } from "../../shared/reporter";

const DSN = "https://9a617c051b1444dba942b0d0e762cf0c@paiq.nl/bugs/3";

const reporter = createReporter({
  dsn: DSN,
  platform: "javascript",
  environment: __WILDHUNT_DEV__ ? "development" : "production",
  release: __WILDHUNT_RELEASE__,
  maxBreadcrumbs: 100,
  ignoreErrors: [
    /ResizeObserver loop/i,
    /Non-Error promise rejection captured/i,
    // The field terminal is offline by design between fixes/reconnects.
    /NetworkError|Failed to fetch|Load failed/i,
    "AbortError",
  ],
});

/** Identify the current player on every subsequent report (or clear it). */
export function setSentryPlayer(info: { userId?: string; name?: string } | null): void {
  reporter.setUser(info?.userId ? { id: info.userId, username: info.name } : null);
}

/** Tag reports with coarse gameplay state so issues can be grouped/filtered. */
export function setSentryGame(
  tags: Record<string, string | number | boolean | undefined>,
): void {
  for (const [k, v] of Object.entries(tags)) reporter.setTag(k, v);
}

/** Attach a one-off structured context block (device/shell capabilities, …). */
export function setSentryContext(key: string, ctx: Record<string, unknown> | null): void {
  reporter.setContext(key, ctx);
}

/** Record a gameplay/voice line as a breadcrumb, so a later crash has a trail. */
export function sentryBreadcrumb(message: string, category = "game"): void {
  reporter.addBreadcrumb(message, category);
}

/** Manually report a caught error (with optional extra context). */
export function captureClientError(err: unknown, extra?: Record<string, unknown>): void {
  reporter.captureException(err, extra);
}
